/**
 * Hook for bottom-sheet drag-to-dismiss behavior.
 *
 * Tracks pointer events on a drag handle, translates the sheet
 * element downward as the user drags, and calls onDismiss when
 * the drag exceeds a threshold. Snaps back on release otherwise.
 *
 * Only activates when the sheet is in bottom-sheet mode (checked
 * via the handle element's visibility — CSS hides it on desktop).
 *
 * Motion durations match the semantic tokens:
 *   dismiss: motion.transition.modal.exit  → dur.quick (150ms)
 *   snap-back: motion.feedback.toggle.flip → dur.pop   (100ms)
 * These are JS-driven animations (imperative translateY), so they
 * use hardcoded values rather than reading CSS custom properties.
 */

import { useRef, useCallback, useEffect, type RefObject } from 'react';

const DISMISS_THRESHOLD = 120; // px — or 30% of sheet height, whichever is smaller
const DISMISS_DURATION = 150; // ms — matches motion.transition.modal.exit (dur.quick)
const SNAP_BACK_DURATION = 100; // ms — matches motion.feedback.toggle.flip (dur.pop)

export interface UseSheetDragOptions {
  sheetRef: RefObject<HTMLDivElement | null>;
  onDismiss?: () => void;
}

export function useSheetDrag({ sheetRef, onDismiss }: UseSheetDragOptions) {
  const startY = useRef(0);
  const currentY = useRef(0);
  const dragging = useRef(false);
  const dismissTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupFn = useRef<(() => void) | null>(null);

  // Clean up pending timeout + transitionend listener on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeout.current !== null) {
        clearTimeout(dismissTimeout.current);
        dismissTimeout.current = null;
      }
      cleanupFn.current?.();
      cleanupFn.current = null;
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only respond to primary pointer (touch or left mouse)
      if (e.button !== 0) return;
      const sheet = sheetRef.current;
      if (!sheet) return;

      dragging.current = true;
      startY.current = e.clientY;
      currentY.current = 0;

      // Capture pointer so moves outside the handle still track
      try {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* noop — element may not support pointer capture */
      }

      // Disable sheet transition during drag for immediate feedback
      sheet.style.transition = 'none';
    },
    [sheetRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      const sheet = sheetRef.current;
      if (!sheet) return;

      const deltaY = e.clientY - startY.current;
      // Only allow dragging downward (positive deltaY)
      const clampedDelta = Math.max(0, deltaY);
      currentY.current = clampedDelta;

      sheet.style.transform = `translateY(${clampedDelta}px)`;

      // Fade overlay proportionally
      const overlay = sheet.closest('.j-modal-overlay') as HTMLElement | null;
      if (overlay) {
        const progress = Math.min(clampedDelta / 300, 1);
        overlay.style.opacity = String(1 - progress * 0.5);
      }
    },
    [sheetRef],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      dragging.current = false;

      const sheet = sheetRef.current;
      if (!sheet) return;

      const threshold = Math.min(DISMISS_THRESHOLD, sheet.offsetHeight * 0.3);
      const overlay = sheet.closest('.j-modal-overlay') as HTMLElement | null;

      if (currentY.current > threshold) {
        // Dismiss — animate out
        sheet.style.transition = `transform ${DISMISS_DURATION}ms ease-out`;
        sheet.style.transform = 'translateY(100%)';
        if (overlay) {
          overlay.style.transition = `opacity ${DISMISS_DURATION}ms ease-out`;
          overlay.style.opacity = '0';
        }
        dismissTimeout.current = setTimeout(() => {
          dismissTimeout.current = null;
          onDismiss?.();
          // Reset inline styles after dismiss callback (safe on detached DOM)
          sheet.style.transition = '';
          sheet.style.transform = '';
          if (overlay) {
            overlay.style.transition = '';
            overlay.style.opacity = '';
          }
        }, DISMISS_DURATION);
      } else {
        // Snap back
        sheet.style.transition = `transform ${SNAP_BACK_DURATION}ms ease-out`;
        sheet.style.transform = '';
        if (overlay) {
          overlay.style.transition = `opacity ${SNAP_BACK_DURATION}ms ease-out`;
          overlay.style.opacity = '';
        }
        // Clean up transition after snap-back completes
        const cleanup = () => {
          sheet.style.transition = '';
          sheet.removeEventListener('transitionend', cleanup);
          cleanupFn.current = null;
        };
        // Clear any previous cleanup listener
        cleanupFn.current?.();
        cleanupFn.current = cleanup;
        sheet.addEventListener('transitionend', cleanup);
      }

      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* noop — element may have been removed or doesn't support capture */
      }
    },
    [sheetRef, onDismiss],
  );

  return { onPointerDown, onPointerMove, onPointerUp };
}
